import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Paper,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import {
  FiberManualRecord,
  Send,
  Person,
  Email,
  Phone,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import emailjs from "emailjs-com";
import { init } from "emailjs-com";

init("user_mNF2hAyVwCH02WN5e7WQV");

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const BackgroundBox = styled(Box)({
  position: "relative",
  minHeight: "100vh",
  background: "linear-gradient(180deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  overflow: "hidden",
  paddingTop: "80px",
  paddingBottom: "80px",
  display: "flex",
  alignItems: "center",
});

const GradientOrb = styled(Box)(({ top, left, bottom, right, color }) => ({
  position: "absolute",
  width: 500,
  height: 500,
  borderRadius: "50%",
  background: color,
  filter: "blur(100px)",
  pointerEvents: "none",
  top,
  left,
  bottom,
  right,
}));

const GridPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.05,
  backgroundImage: `linear-gradient(rgba(148, 163, 184, 0.1) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(148, 163, 184, 0.1) 1px, transparent 1px)`,
  backgroundSize: "50px 50px",
  pointerEvents: "none",
});

const FormCard = styled(Paper)({
  background: "rgba(0, 0, 0, 0.7)",
  backdropFilter: "blur(20px)",
  borderRadius: 24,
  border: "1px solid rgba(193, 227, 40, 0.3)",
  padding: "48px",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(193, 227, 40, 0.05) 0%, transparent 100%)",
    pointerEvents: "none",
  },
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    borderRadius: 12,
    "& fieldset": {
      borderColor: "rgba(193, 227, 40, 0.3)",
      transition: "all 0.3s ease",
    },
    "&:hover fieldset": {
      borderColor: "rgba(193, 227, 40, 0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#c1e328",
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(148, 163, 184, 1)",
    "&.Mui-focused": {
      color: "#c1e328",
    },
  },
  "& .MuiOutlinedInput-input": {
    "&::placeholder": {
      color: "rgba(148, 163, 184, 0.5)",
      opacity: 1,
    },
  },
});

const SubmitButton = styled(Button)({
  background: "linear-gradient(135deg, #c1e328 0%, #a0c020 100%)",
  borderRadius: 12,
  padding: "16px 48px",
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  color: "#000",
  letterSpacing: 0.5,
  boxShadow: "0 4px 14px 0 rgba(193, 227, 40, 0.4)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: "linear-gradient(135deg, #d0f030 0%, #c1e328 100%)",
    boxShadow: "0 6px 20px 0 rgba(193, 227, 40, 0.5)",
    transform: "translateY(-2px)",
  },
  "& .MuiButton-endIcon": {
    marginLeft: "12px",
    transition: "transform 0.3s ease",
  },
  "&:hover .MuiButton-endIcon": {
    transform: "translateX(4px)",
  },
});

const InfoCard = styled(Paper)({
  background: "rgba(0, 0, 0, 0.6)",
  backdropFilter: "blur(10px)",
  borderRadius: 16,
  border: "1px solid rgba(193, 227, 40, 0.3)",
  padding: "24px",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    borderColor: "rgba(193, 227, 40, 0.5)",
    boxShadow: "0 10px 30px rgba(193, 227, 40, 0.15)",
  },
});

export const Contact = () => {
  const form = useRef();
  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    // Scroll to top when component mounts - useLayoutEffect runs before paint
    // The scrollable container is the .main-content Box in App.js
    const scrollContainer = document.querySelector('.main-content');
    if (scrollContainer) {
      scrollContainer.scrollTo(0, 0);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();

    emailjs
      .sendForm(
        "service_08qhiud",
        "template_kxor1li",
        form.current,
        "user_mNF2hAyVwCH02WN5e7WQV"
      )
      .then(
        (result) => {
          toast.success("Message sent successfully!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        },
        (error) => {
          toast.error("Failed to send message. Please try again.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }
      );
    e.target.reset();
  };

  return (
    <>
      <BackgroundBox>
        {/* Background Elements */}
        <GradientOrb top="20%" left={-256} color="rgba(193, 227, 40, 0.1)" />
        <GradientOrb bottom="20%" right={-256} color="rgba(193, 227, 40, 0.1)" />
        <GridPattern />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Section Header */}
          <Box
            textAlign="center"
            mb={8}
            sx={{
              animation: isVisible ? `${fadeInUp} 0.8s ease-out` : "none",
            }}
          >
            <Chip
              icon={
                <FiberManualRecord
                  sx={{
                    fontSize: 8,
                    animation: `${pulse} 2s ease-in-out infinite`,
                  }}
                />
              }
              label="GET IN TOUCH"
              sx={{
                background: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(193, 227, 40, 0.3)",
                color: "#c1e328",
                fontWeight: 700,
                letterSpacing: 3,
                fontSize: "0.625rem",
                mb: 3,
              }}
            />

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                fontWeight: 700,
                color: "#fff",
                letterSpacing: -1,
                mb: 2,
              }}
            >
              Contact{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #c1e328 0%, #9fcc2e 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Us
              </Box>
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: "rgba(148, 163, 184, 1)",
                maxWidth: 700,
                mx: "auto",
                lineHeight: 1.7,
                fontSize: { xs: "1rem", md: "1.125rem" },
              }}
            >
              Have questions? We'd love to hear from you. Send us a message
              and we'll respond as soon as possible.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Contact Form */}
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  animation: isVisible ? `${fadeInUp} 0.8s ease-out 0.2s both` : "none",
                }}
              >
                <FormCard elevation={0}>
                    <form ref={form} onSubmit={sendEmail}>
                    <Grid container spacing={3}>
                      {/* Full Name */}
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label="Full Name"
                          name="user_name"
                          placeholder="Enter your name"
                          required
                          InputProps={{
                            startAdornment: (
                              <Person sx={{ color: "rgba(148, 163, 184, 1)", mr: 1 }} />
                            ),
                          }}
                        />
                      </Grid>

                      {/* Email */}
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label="Email Address"
                          name="user_email"
                          type="email"
                          placeholder="your@email.com"
                          required
                          InputProps={{
                            startAdornment: (
                              <Email sx={{ color: "rgba(148, 163, 184, 1)", mr: 1 }} />
                            ),
                          }}
                        />
                      </Grid>

                      {/* Phone */}
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          label="Phone Number"
                          name="user_phone"
                          type="tel"
                          placeholder="+91 12345 67890"
                          required
                          InputProps={{
                            startAdornment: (
                              <Phone sx={{ color: "rgba(148, 163, 184, 1)", mr: 1 }} />
                            ),
                          }}
                        />
                      </Grid>

                      {/* Message */}
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label="Message"
                          name="message"
                          placeholder="Tell us about your requirements..."
                          multiline
                          rows={6}
                          required
                        />
                      </Grid>

                      {/* Submit Button */}
                      <Grid item xs={12} >
                        <SubmitButton
                          type="submit"
                          fullWidth
                          endIcon={<Send />}
                        >
                          Send Message
                        </SubmitButton>
                      </Grid>
                    </Grid>
                    </form>
                </FormCard>
              </Box>
            </Grid>

            {/* Contact Info */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  animation: isVisible ? `${fadeInUp} 0.8s ease-out 0.4s both` : "none",
                }}
              >
                <InfoCard elevation={0}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#c1e328",
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    Contact Information
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(148, 163, 184, 1)",
                      lineHeight: 1.8,
                      mb: 3,
                    }}
                  >
                    Feel free to reach out to us for any enquiries, partnerships,
                    or to learn more about our innovative robotic solutions.
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(148, 163, 184, 1)",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        mb: 1,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Email
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#fff",
                        fontWeight: 500,
                      }}
                    >
                      aircorpofficial@gmail.com
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(148, 163, 184, 1)",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        mb: 1,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Response Time
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#fff",
                        fontWeight: 500,
                      }}
                    >
                      Within 24 hours
                    </Typography>
                  </Box>
                </InfoCard>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </BackgroundBox>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </>
  );
};

export default Contact;
